<template>
  <div class="gr-detail">
    <!-- Full bleed. The first draft nested this in the 1200px container and
         capped it with max-height, which meant the 16/7 ratio held the width
         down to 914px inside a 1200px column. -->
    <div class="gr-detail__banner">
      <img
        v-if="hasBanner && !hasImageError"
        :ref="setImageElement"
        :src="activity.img"
        :alt="activity.title"
        class="gr-detail__banner-img"
        @error="hasImageError = true"
      />
      <!-- A missing banner and an expired link land on the same neutral
           stand-in, and both drop the alt text because the stand-in carries no
           information about the activity. -->
      <div v-else class="gr-detail__banner-img gr-detail__banner-img--fallback">
        <v-icon icon="mdi-image-off-outline" size="40" aria-hidden="true" />
      </div>

      <div class="gr-detail__scrim">
        <div class="gr-container gr-detail__scrim-inner">
          <p class="gr-detail__flags">
            <span
              class="gr-detail__flag"
              :class="{
                'gr-detail__flag--ended': activity.isExpired && !isFinished,
              }"
            >
              <v-icon
                v-if="isFinished"
                icon="mdi-calendar-check-outline"
                size="14"
                aria-hidden="true"
              />
              {{
                activity.isExpired && !isFinished
                  ? "Sudah berakhir"
                  : finishedLabel
              }}
            </span>
            <span class="gr-detail__flag-date">
              {{ activity.date.displayText }}
            </span>
          </p>
          <h1 class="gr-detail__title">{{ activity.title }}</h1>
          <p class="gr-detail__place">
            <v-icon icon="mdi-map-marker" size="16" aria-hidden="true" />
            {{ activity.location.city }}, {{ activity.location.province }}
          </p>
        </div>
      </div>
    </div>

    <div class="gr-container gr-detail__grid">
      <div class="gr-detail__main">
        <header class="gr-detail__head">
          <p class="gr-detail__description multiline-text">
            {{ activity.description }}
          </p>
        </header>

        <section class="gr-detail__section">
          <p class="gr-eyebrow" aria-hidden="true">
            <span class="gr-eyebrow__index">01</span>
            <span class="gr-eyebrow__rule" />
          </p>
          <h2 class="gr-detail__h2">Waktu dan lokasi</h2>
          <dl class="gr-detail__facts">
            <dt>Tanggal aktifitas</dt>
            <dd>{{ activity.date.displayText }}</dd>
            <!-- The API returns startTime and endTime and the composable maps
                 them, but no canonical view renders them, so a volunteer could
                 not tell what time the activity runs. -->
            <template v-if="timeOfDay">
              <dt>Jam pelaksanaan</dt>
              <dd>{{ timeOfDay }}</dd>
            </template>
            <dt>Lokasi Aktivitas</dt>
            <dd>
              {{ activity.location.address }} {{ activity.location.city }},
              {{ activity.location.province }}
            </dd>
            <template v-if="!isFinished">
              <dt>Batas pendaftaran</dt>
              <dd>{{ formatDate(activity.registrationDeadline) }}</dd>
            </template>
          </dl>
        </section>

        <section class="gr-detail__section">
          <p class="gr-eyebrow" aria-hidden="true">
            <span class="gr-eyebrow__index">02</span>
            <span class="gr-eyebrow__rule" />
          </p>
          <h2 class="gr-detail__h2">Detail Aktivitas</h2>
          <dl class="gr-detail__facts">
            <!-- "Nama Pekerjaan" is not rendered: the API has a single
                 `assignment` column and the composable maps it to both
                 `jobName` and `volunteerTasks`, so canonical prints the same
                 sentence twice under two labels. One row, once. -->
            <dt>Relawan Dibutuhkan</dt>
            <dd>{{ activity.totalVolunteers }} Orang</dd>
            <dt>Waktu Kerja</dt>
            <dd>{{ activity.totalWorkHours }} Jam</dd>
            <dt>Tugas Relawan</dt>
            <dd class="multiline-text">{{ activity.volunteerTasks }}</dd>
            <dt>Kriteria Relawan</dt>
            <dd class="multiline-text">{{ activity.volunteerCriteria }}</dd>
            <dt>Perlengkapan Relawan</dt>
            <dd class="multiline-text">{{ activity.volunteerEquipment }}</dd>
            <dt>Informasi Tambahan</dt>
            <dd class="multiline-text">{{ activity.additionalInformation }}</dd>
            <dt>Lampiran pendukung</dt>
            <dd>
              <div v-if="attachments.length" class="gr-detail__attachments">
                <template
                  v-for="(url, index) in attachments"
                  :key="`attachment-${index}`"
                >
                  <button
                    v-if="isImageUrl(url)"
                    type="button"
                    class="gr-detail__attachment"
                    :aria-label="`Lihat lampiran ${index + 1}`"
                    @click="openImageDialog(url)"
                  >
                    <img
                      :src="url"
                      :alt="`Lampiran ${index + 1}`"
                      class="gr-detail__attachment-img"
                      loading="lazy"
                    />
                  </button>
                  <!-- Anything that is not an image opens the file itself.
                       Canonical sent every attachment to an image dialog, so a
                       PDF opened a dialog that could not render it and offered
                       no way to save it. -->
                  <a
                    v-else
                    :href="url"
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    class="gr-detail__attachment"
                  >
                    <span class="gr-detail__attachment-doc">
                      <v-icon
                        icon="mdi-file-download-outline"
                        size="22"
                        aria-hidden="true"
                      />
                      Dokumen {{ index + 1 }}
                    </span>
                  </a>
                </template>
              </div>

              <span v-else>Tidak ada lampiran</span>
            </dd>
            <!-- The report belongs with the other files this activity hands
                 the public, not in a slab of its own further down the page.
                 Only GRC activities owe one, and only once they have
                 finished. -->
            <template v-if="showLpj">
              <dt>Laporan pertanggungjawaban</dt>
              <dd><ActivityLpj :slug="activity.slug" :eligible="true" /></dd>
            </template>
          </dl>
        </section>
      </div>

      <!-- Rendered at every width. Canonical hides this column below md, which
           takes the countdown, the execution summary, the organizer contact and
           the share row away from phones entirely; here it moves under the
           content instead. -->
      <aside class="gr-detail__aside">
        <div class="gr-detail__panel gr-section--panel">
          <section v-if="isFinished">
            <h2 class="gr-detail__panel-title">Ringkasan pelaksanaan</h2>
            <!-- Only stored fields. A blank field drops its row instead of
                 being filled with an estimate. -->
            <dl class="gr-detail__summary">
              <template v-for="row in executionSummary" :key="row.label">
                <dt>{{ row.label }}</dt>
                <dd>{{ row.value }}</dd>
              </template>
            </dl>
          </section>

          <section v-else-if="!activity.isExpired">
            <ClientOnly>
              <div
                class="gr-detail__countdown"
                :class="{ 'gr-detail__countdown--urgent': isUrgent }"
              >
                <p class="gr-detail__countdown-label">
                  Pendaftaran ditutup dalam:
                </p>
                <div class="gr-detail__countdown-row">
                  <div
                    v-for="unit in countdownUnits"
                    :key="unit.label"
                    class="gr-detail__countdown-unit"
                  >
                    <span class="gr-detail__countdown-number">
                      {{ unit.value }}
                    </span>
                    <span class="gr-detail__countdown-text">
                      {{ unit.label }}
                    </span>
                  </div>
                </div>
              </div>
              <template #fallback>
                <div class="gr-detail__countdown">
                  <p class="gr-detail__countdown-label">
                    Pendaftaran ditutup dalam:
                  </p>
                  <div class="gr-detail__countdown-row">
                    <div
                      v-for="label in ['Hari', 'Jam', 'Menit', 'Detik']"
                      :key="label"
                      class="gr-detail__countdown-unit"
                    >
                      <span class="gr-detail__countdown-number">00</span>
                      <span class="gr-detail__countdown-text">
                        {{ label }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </ClientOnly>
            <p
              v-if="remainingSpots <= 1"
              class="gr-detail__spots"
              role="status"
            >
              <v-icon icon="mdi-alert-circle" size="18" aria-hidden="true" />
              Hanya tersisa {{ remainingSpots }} posisi!
            </p>
          </section>

          <button
            v-if="!isFinished && !activity.isMine && !activity.isExpired"
            type="button"
            class="gr-btn gr-btn--solid gr-btn--on-panel gr-detail__cta"
            :disabled="activity.isExpired"
            @click="handleRegisterVolunteer"
          >
            Jadi relawan
          </button>

          <a
            :href="whatsappOrganizerUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="gr-btn gr-btn--outline gr-btn--on-panel gr-detail__cta"
          >
            <v-icon icon="mdi-whatsapp" size="18" aria-hidden="true" />
            WhatsApp Organizer
          </a>

          <div class="gr-detail__share">
            <p class="gr-detail__share-label">Bagikan</p>
            <div class="gr-detail__share-row">
              <button
                type="button"
                class="gr-detail__share-btn"
                aria-label="Bagikan lewat WhatsApp"
                @click="shareOnWhatsApp"
              >
                <v-icon icon="mdi-whatsapp" size="20" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="gr-detail__share-btn"
                aria-label="Bagikan lewat Facebook"
                @click="shareOnFacebook"
              >
                <v-icon icon="mdi-facebook" size="20" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="gr-detail__share-btn"
                aria-label="Bagikan lewat Twitter"
                @click="shareOnTwitter"
              >
                <v-icon icon="mdi-twitter" size="20" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <ActivityDocumentations v-if="activity.slug" :slug="activity.slug" />
    <ActivityAttachmentDialog
      :open="isOpenDialogImage"
      :img-src="selectedImageSrc"
      @close="closeImageDialog"
    />
  </div>
</template>

<script lang="ts" setup>
import ActivityLpj from "@/features/Activity/views/components/ActivityLpj.vue";
import ActivityDocumentations from "@/features/Activity/views/components/ActivityDocumentations.vue";
import ActivityAttachmentDialog from "@/features/Activity/views/components/ActivityAttachmentDialog.vue";
import {
  ACTIVITY_STATUS_LABEL,
  type ActivityStatus,
} from "~/common/utils/activityStatus";
import {
  formatDate,
  getShareUrls,
  getWhatsAppOrganizerUrl,
} from "~/common/utils";
import type { DetailActivityWithStatus } from "~/features/Activity/composables/useActivityDetail";
import { useCountdown } from "~/features/Activity/composables/useCountdown";

const { activity } = defineProps<{ activity: DetailActivityWithStatus }>();

const emit = defineEmits<{ registerVolunteer: [] }>();

// Finishedness comes from the shared predicate via the detail composable.
// Never re-derived here from isExpired, endDate, or a status string, so there
// is exactly one rule in the codebase.
const isFinished = computed(() => activity.isFinished);

const showLpj = computed(() => isFinished.value && activity.usesGrc);

const hasImageError = ref(false);
const hasBanner = computed(() => Boolean(activity.img));

const imageElement = ref<HTMLImageElement | null>(null);
const setImageElement = (
  element: Element | ComponentPublicInstance | null,
): void => {
  imageElement.value = element instanceof HTMLImageElement ? element : null;
};
onMounted(() => {
  const image = imageElement.value;
  if (image?.complete && image.naturalWidth === 0) hasImageError.value = true;
});

const finishedLabel = computed(
  () =>
    ACTIVITY_STATUS_LABEL[activity.status as ActivityStatus] ??
    ACTIVITY_STATUS_LABEL.FINISHED,
);

const attachments = computed(() => activity.attachmentUrls ?? []);

const trimmed = (value: string | undefined): string => value?.trim() ?? "";

// Rendered only when the API actually supplied a time; never guessed.
const timeOfDay = computed(() => {
  const time = activity.date?.time;
  const start = trimmed(time?.start);
  const end = trimmed(time?.end);
  if (start && end) return `${start} - ${end} WIB`;
  const single = start || end;
  return single ? `${single} WIB` : "";
});

// Only stored activity fields. A blank field omits its row instead of being
// filled with an estimate.
const executionSummary = computed(() => {
  const rows: Array<{ label: string; value: string }> = [];
  const push = (label: string, value: string | undefined | null): void => {
    const text = value?.toString().trim();
    if (text) rows.push({ label, value: text });
  };

  push("Tanggal pelaksanaan", activity.date?.displayText);
  push(
    "Lokasi",
    [
      activity.location?.address,
      activity.location?.city,
      activity.location?.province,
    ]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(", "),
  );
  push(
    "Durasi kerja",
    activity.totalWorkHours ? `${activity.totalWorkHours} jam` : "",
  );
  // Stated need at planning time, never attendance or turnout.
  push(
    "Kebutuhan relawan",
    activity.totalVolunteers > 0
      ? `${activity.totalVolunteers} orang dibutuhkan`
      : "",
  );

  return rows;
});

const remainingSpots = computed(() =>
  activity ? activity.totalVolunteers - activity.filledSpots : 0,
);

const whatsappOrganizerUrl = computed(() =>
  activity ? getWhatsAppOrganizerUrl(activity) : "#",
);

const { countdownData, isUrgent } = useCountdown(
  computed(() => activity?.registrationDeadline || new Date().toISOString()),
);

const countdownUnits = computed(() => [
  { label: "Hari", value: countdownData.days },
  { label: "Jam", value: countdownData.hours },
  { label: "Menit", value: countdownData.minutes },
  { label: "Detik", value: countdownData.seconds },
]);

const shareUrls = computed(() =>
  activity
    ? getShareUrls(activity.title)
    : { whatsapp: "", facebook: "", twitter: "" },
);

const openExternal = (url: string): void => {
  if (typeof window !== "undefined") window.open(url, "_blank");
};
const shareOnWhatsApp = (): void => openExternal(shareUrls.value.whatsapp);
const shareOnFacebook = (): void => openExternal(shareUrls.value.facebook);
const shareOnTwitter = (): void => openExternal(shareUrls.value.twitter);

const handleRegisterVolunteer = (): void => emit("registerVolunteer");

const isOpenDialogImage = ref(false);
const selectedImageSrc = ref("");
// The dialog takes a one-way `:open` prop plus a `@close` event rather than
// `v-model`, so Vuetify never sees the binding it uses to restore focus and
// drops it on `<body>` when the dialog closes. A keyboard user who pressed
// Escape would then have to tab from the top of the document to get back to
// the attachment they opened (WCAG 2.4.3). Hold the trigger ourselves.
let dialogTrigger: HTMLElement | null = null;

const openImageDialog = (url: string): void => {
  dialogTrigger =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  selectedImageSrc.value = url;
  isOpenDialogImage.value = true;
};

const closeImageDialog = (): void => {
  isOpenDialogImage.value = false;
  const trigger = dialogTrigger;
  dialogTrigger = null;
  if (trigger) void nextTick(() => trigger.focus({ preventScroll: true }));
};

const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".svg",
  ".bmp",
  ".tiff",
  ".avif",
];

function isImageUrl(url: string): boolean {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return IMAGE_EXTENSIONS.some((ext) => pathname.endsWith(ext));
  } catch {
    return IMAGE_EXTENSIONS.some((ext) => url.toLowerCase().includes(ext));
  }
}
</script>

<style scoped src="@/features/Activity/views/activity-detail.css"></style>
